import { motion } from 'framer-motion';
import { Award, Heart, Target, Users, CheckCircle, TrendingUp } from 'lucide-react';

/**
 * About Page Component
 * Story of LC Performance, Luke's background, mission, and values
 */
export default function About() {
  return (
    <div className="min-h-screen">
      <HeroSection />
      <StorySection />
      <MissionSection />
      <ValuesSection />
      <CertificationsSection />
    </div>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="relative py-20 bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500 text-white overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            About LC Performance
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Founded by Luke Crisanti, dedicated lifestyle and fitness coach helping clients achieve
            their ultimate potential through personalized training and daily support.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// Story Section - Luke's background
function StorySection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 overflow-hidden shadow-2xl">
              <img 
                src="/luke-crisanti.png" 
                alt="Luke Crisanti - Founder & Head Coach"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 15%' }}
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent-500 rounded-full opacity-20 blur-2xl"></div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-primary-50 px-4 py-2 rounded-full mb-6">
              <Heart className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-semibold text-primary-900">The LC Performance Story</span>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Transforming Lives Through Fitness
            </h2>

            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Luke Crisanti founded LC Performance with a clear mission: to provide elite personal training
                that goes beyond the gym. Based in Palm Beach, Luke has built a reputation as a dedicated
                lifestyle and fitness coach who genuinely cares about each client's journey.
              </p>

              <p>
                What started as a passion for helping individuals achieve their fitness goals has evolved
                into a comprehensive lifestyle coaching experience. Luke combines his deep knowledge of
                exercise science, nutrition, and habit formation to create sustainable transformations
                that last.
              </p>

              <p>
                Today, LC Performance serves clients both locally in Palm Beach through 1-on-1 in-person
                sessions and globally through online coaching programs. Every program is built around
                the individual—their goals, their lifestyle, and their unique circumstances.
              </p>

              <p className="font-semibold text-primary-600">
                "I don't just train bodies—I transform lives. Every client deserves personalized attention,
                daily support, and a coach who's committed to their success."
              </p>
              <p className="text-sm text-gray-600">— Luke Crisanti, Founder</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Mission Section
function MissionSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Our Mission
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            To empower individuals to achieve their ultimate fitness and lifestyle goals through
            personalized training, unwavering support, and proven methodologies that create
            lasting transformation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <Target className="w-12 h-12" />,
              title: 'Personalization',
              description: 'Every program is custom-designed around your unique goals, abilities, and lifestyle.'
            },
            {
              icon: <Users className="w-12 h-12" />,
              title: 'Commitment',
              description: 'Daily guidance and support to keep you accountable and motivated throughout your journey.'
            },
            {
              icon: <TrendingUp className="w-12 h-12" />,
              title: 'Results',
              description: 'Proven training methods and nutrition strategies that deliver measurable, sustainable outcomes.'
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-white/5 backdrop-blur-sm rounded-2xl hover:bg-white/10 transition-all duration-300"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500">
                {item.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-gray-300 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Values Section
function ValuesSection() {
  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Client-Centered',
      description: 'Your success is our success. We put your needs, goals, and wellbeing first in everything we do.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Excellence',
      description: 'We maintain the highest standards in coaching, continuously learning and improving our methods.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Integrity',
      description: 'Honest, transparent communication and realistic expectations built on trust and accountability.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Community',
      description: 'Building a supportive environment where clients motivate and inspire each other to succeed.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Sustainable Results',
      description: 'We focus on long-term lifestyle changes, not quick fixes or extreme measures.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Continuous Growth',
      description: 'We believe in constant improvement—for both our clients and ourselves as coaches.'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Core Values
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The principles that guide everything we do at LC Performance
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-16 h-16 mb-6 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 text-white group-hover:scale-110 transition-transform duration-300">
                {value.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {value.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Certifications Section
function CertificationsSection() {
  const certifications = [
    'Certified Personal Trainer (CPT)',
    'Nutrition Coach Certification',
    'Strength & Conditioning Specialist',
    'Functional Movement Specialist',
    'Sports Performance Coach',
    'Online Coaching Certification'
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-accent-50 px-4 py-2 rounded-full mb-6">
            <Award className="w-4 h-4 text-accent-600" />
            <span className="text-sm font-semibold text-accent-900">Qualifications & Expertise</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Professionally Trained & Certified
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Luke maintains industry-leading certifications and continuously expands his knowledge
            to provide the best possible service to clients.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {certifications.map((cert, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl hover:bg-primary-50 transition-colors duration-300"
            >
              <CheckCircle className="w-6 h-6 text-primary-600 flex-shrink-0" />
              <span className="font-medium text-gray-900">{cert}</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-6">
            Ready to work with a certified professional who cares about your success?
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-500 text-white rounded-full font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            Start Your Journey
          </a>
        </motion.div>
      </div>
    </section>
  );
}
